import notifee, { RepeatFrequency, Trigger, TriggerType } from '@notifee/react-native';
import { Platform } from 'react-native';
import type {
  ActivationTaskId,
  ActivationTrackerPhase,
  LocationScopedActivationTaskId,
} from '../redux/ownerActivationSlice';
import { isLocationScopedTask } from '../redux/ownerActivationSlice';
import { hasLocalNotificationPermission } from '../services/localPushNotification';
import {
  scheduleBackgroundNotification,
} from './Background';

export const GETTING_STARTED_NOTIFICATION_ID = 'getting-started-daily';
export const GETTING_STARTED_PUSH_TYPE = 'getting_started';

type GettingStartedCopy = {
  title: string;
  body: string;
};

/**
 * Industry-style activation nudges (unfinished progress / loss aversion /
 * low-friction CTA). Kept separate from tracker list labels.
 */
const GETTING_STARTED_NOTIFICATION_COPY: Record<
  LocationScopedActivationTaskId,
  GettingStartedCopy
> = {
  add_product: {
    title: 'Add your first product',
    body: 'Add one item now — it takes about a minute and unlocks selling.',
  },
  first_sale: {
    title: 'Complete your first sale',
    body: 'When you’re ready, start tracking your cashflow and stock.',
  },
  explore_backoffice: {
    title: 'Explore Back Office',
    body: 'Take a look around — your cashflow, stock, and orders are all there.',
  },
  add_staff: {
    title: 'Add a staff member',
    body: 'Got someone helping at the shop? Give them their own access.',
  },
};

export const getGettingStartedNotificationCopy = (
  taskId: LocationScopedActivationTaskId,
): GettingStartedCopy => GETTING_STARTED_NOTIFICATION_COPY[taskId];

/** Pure gate: which task should the daily 10am nudge target, if any. */
export const resolveGettingStartedNotificationTarget = (args: {
  isLoggedIn: boolean;
  phase: ActivationTrackerPhase;
  highlightedTaskId: ActivationTaskId | null;
}): LocationScopedActivationTaskId | null => {
  const { isLoggedIn, phase, highlightedTaskId } = args;
  if (!isLoggedIn) return null;
  if (phase !== 'active') return null;
  if (!highlightedTaskId || !isLocationScopedTask(highlightedTaskId)) {
    return null;
  }
  return highlightedTaskId;
};

export const isGettingStartedNotificationData = (
  data: unknown,
): data is {
  type: string;
  taskId: LocationScopedActivationTaskId;
} => {
  if (!data || typeof data !== 'object') return false;
  const payload = data as Record<string, unknown>;
  const taskId = String(payload.taskId || '') as ActivationTaskId;
  return (
    String(payload.type || '') === GETTING_STARTED_PUSH_TYPE &&
    isLocationScopedTask(taskId)
  );
};

const buildDailyTenAmTrigger = (): Trigger => {
  const date = new Date();
  date.setHours(10);
  date.setMinutes(0);
  date.setSeconds(0);
  date.setMilliseconds(0);

  if (date.getTime() <= Date.now()) {
    date.setDate(date.getDate() + 1);
  }

  return {
    type: TriggerType.TIMESTAMP,
    timestamp: date.getTime(),
    repeatFrequency: RepeatFrequency.DAILY,
  };
};

export const scheduleGettingStartedNotification = async (
  taskId: LocationScopedActivationTaskId,
): Promise<string | undefined> => {
  if (Platform.OS === 'web') return undefined;

  // Check only — never prompt from background sync.
  const permitted = await hasLocalNotificationPermission();
  if (!permitted) return undefined;

  const { title, body } = getGettingStartedNotificationCopy(taskId);
  const trigger = buildDailyTenAmTrigger();

  await notifee.cancelTriggerNotification(GETTING_STARTED_NOTIFICATION_ID);
  return scheduleBackgroundNotification({
    id: GETTING_STARTED_NOTIFICATION_ID,
    title,
    body,
    trigger,
    data: {
      type: GETTING_STARTED_PUSH_TYPE,
      taskId,
    },
  });
};

export const cancelGettingStartedNotification = async (): Promise<void> => {
  if (Platform.OS === 'web') return;
  try {
    await Promise.all([
      notifee.cancelTriggerNotification(GETTING_STARTED_NOTIFICATION_ID),
      notifee.cancelNotification(GETTING_STARTED_NOTIFICATION_ID),
    ]);
  } catch (error) {
    console.log('Error cancelling getting started notification:', error);
  }
};

/**
 * Schedule or cancel the daily Getting Started nudge based on current
 * activation highlight. Always cancel+reschedule so baked title/body match
 * the current highlighted task.
 */
export const syncGettingStartedNotification = async (args: {
  isLoggedIn: boolean;
  phase: ActivationTrackerPhase;
  highlightedTaskId: ActivationTaskId | null;
}): Promise<LocationScopedActivationTaskId | null> => {
  const target = resolveGettingStartedNotificationTarget(args);
  if (!target) {
    await cancelGettingStartedNotification();
    return null;
  }
  await scheduleGettingStartedNotification(target);
  return target;
};
